import Box from "@mui/material/Box";
import { visuallyHidden } from "@mui/utils";

import {
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { MatrixAxis, MatrixRow } from "@/content/landing/types";
import { LandingImage } from "../LandingPrimitives";

/** The column's printed label; a nested axis carries it on `label`. */
const axisLabel = (axis: MatrixAxis): string =>
  typeof axis === "string" ? axis : axis.label;

/** Not `marketingPalette.hairline`: the published table rules the same 10% of a different ink. */
const RULE = "1px solid rgba(7, 23, 39, 0.1)";

/** `side-compare-title_cell`: one cell, ruled left and bottom; the card supplies the other two sides. */
const COMPARE_CELL = {
  height: "64px",
  p: "0 20px",
  fontSize: 15,
  lineHeight: "22.5px",
  fontWeight: 400,
  color: marketingPalette.ink,
  textAlign: "left",
  verticalAlign: "middle",
  borderLeft: RULE,
  borderBottom: RULE,
  [NOT_WIDE]: {
    height: "67.2px",
    p: "0 12px",
    fontSize: 15.5,
    lineHeight: "23.25px",
  },
  [MID]: { height: "80px", fontSize: 15, lineHeight: "19.5px" },
  [TINY]: { height: "83.2px" },
} as const;

/**
 * `.side-compare-title_cell` keeps its height whatever it holds: a value two
 * lines taller than the cell overflows it on the published table rather than
 * growing the row, which a table cell would otherwise do.
 */
const CELL_FIT = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  // The cell's own height less its bottom rule, which the cell counts in.
  height: "63px",
  overflow: "visible",
  [NOT_WIDE]: { height: "66.2px" },
  [MID]: { height: "79px" },
  [TINY]: { height: "82.2px" },
} as const;

/** Row labels take the muted grey and the values beside them the page's ink, not the other way round. */
const COMPARE_LABEL = {
  ...COMPARE_CELL,
  color: marketingPalette.inkSubtle,
} as const;

/** The label column's own width, which the published table pins rather than shares. */
const COMPARE_LABEL_WIDTH = 224;

/**
 * Below the wide step the published table drops this column outright; ours
 * collapses it so the row header stays in the accessibility tree.
 */
const LABEL_COLUMN = {
  width: `${COMPARE_LABEL_WIDTH}px`,
  [NOT_WIDE]: { width: 0, p: 0, borderLeftWidth: 0 },
} as const;

const LABEL_TEXT = { [NOT_WIDE]: visuallyHidden } as const;

/** `side-compare-mobile-title`: that repeated label, over the value it names. */
const STACKED_LABEL = {
  display: "none",
  [NOT_WIDE]: {
    display: "block",
    mb: "6.08px",
    fontSize: 12,
    lineHeight: "14.4px",
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: marketingPalette.inkSubtle,
    // The muted grey held one step further back, so it stays behind the value.
    opacity: 0.8,
  },
  [TINY]: { mb: "4.8px" },
} as const;

/** `bright-cell-info`: the icon and the value it qualifies, on one line. */
const CELL_LINE = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  // `side-compare-title` strokes the value: a shade heavier, without moving a glyph.
  WebkitTextStroke: "0.3px currentColor",
  [TINY]: { gap: "6.4px" },
} as const;

/** `side-compare-icon`: the flag a "Company based in" row sets beside its country. */
const CELL_ICON = { width: "20px", height: "auto" } as const;

/** `cell-logo` / `.is-bigger`: row 0 is this page's own product, drawn a size up. */
const LOGO_WIDTH = [
  { width: "192px", [TINY]: { width: "160px" } },
  { width: "144px", [TINY]: { width: "112px" } },
] as const;

/** `compare-bright-hero-table_box`: the hero's copy is pinned to this; the section's fills its width. */
const HERO_TABLE_WIDTH = 800;

/** `.is-green` / `.is-red`: row 0 is this page's product, washed green; the rest red. */
const VERDICT = ["rgba(240, 249, 242, 0.8)", "rgb(252, 243, 243)"] as const;

/** `bright-cell-icon.is-green` / `.is-red`: the same verdict, as a trend. */
const VERDICT_ARROW_INK = ["#3CBA58", "#DA2424"] as const;

function VerdictArrow({ down }: { down: boolean }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      sx={{
        flex: "0 0 auto",
        width: "16px",
        height: "16px",
        color: VERDICT_ARROW_INK[down ? 1 : 0],
        [TINY]: { display: "none" },
      }}
    >
      <path d="M12 20V4" />
      <path d={down ? "M5 13l7 7 7-7" : "M5 11l7-7 7 7"} />
    </Box>
  );
}

/**
 * The versus table the comparison pages draw. A real `<table>` with a collapsed
 * header column rather than the published grid of `div`s, so a screen reader
 * gets each row header once.
 */
export default function MatrixTable({
  columns,
  rows,
  verdict = false,
}: {
  columns: ReadonlyArray<MatrixAxis>;
  rows: ReadonlyArray<MatrixRow>;
  /** Wash each provider's column with the published table's verdict colour. */
  verdict?: boolean;
}) {
  return (
    <Box
      sx={[
        {
          overflow: "hidden",
          borderRadius: "8px",
          borderTop: RULE,
          borderRight: RULE,
          backgroundColor: "common.white",
        },
        verdict ? { maxWidth: `${HERO_TABLE_WIDTH}px`, mx: "auto" } : {},
      ]}
    >
      <Box
        component="table"
        sx={{
          width: 1,
          tableLayout: "fixed",
          // Not `collapse`: cells rule two sides only, and a collapsed table
          // paints half of an edge rule outside itself.
          borderCollapse: "separate",
          borderSpacing: 0,
        }}
      >
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={[COMPARE_LABEL, LABEL_COLUMN]} />
            {rows.map((row, index) => (
              <Box component="th" scope="col" key={index} sx={COMPARE_CELL}>
                {row.logo ? (
                  <LandingImage
                    src={row.logo}
                    alt={row.label ?? ""}
                    sx={LOGO_WIDTH[index === 0 ? 0 : 1]}
                  />
                ) : (
                  row.label
                )}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {columns.map((axis, columnIndex) => (
            <Box
              component="tr"
              // Two columns can carry one label; the list is static.
              key={columnIndex}
              sx={
                !verdict && columnIndex % 2 === 1
                  ? { backgroundColor: marketingPalette.surface }
                  : {}
              }
            >
              <Box
                component="th"
                scope="row"
                sx={[COMPARE_LABEL, LABEL_COLUMN]}
              >
                <Box component="span" sx={LABEL_TEXT}>
                  {axisLabel(axis)}
                </Box>
              </Box>
              {rows.map((row, rowIndex) => {
                const cell = row.cells[columnIndex];
                return (
                  <Box
                    component="td"
                    key={rowIndex}
                    sx={[
                      COMPARE_CELL,
                      verdict
                        ? {
                            backgroundColor:
                              VERDICT[Math.min(rowIndex, VERDICT.length - 1)],
                          }
                        : {},
                    ]}
                  >
                    <Box sx={CELL_FIT}>
                      {rowIndex === 0 ? (
                        <Box aria-hidden sx={STACKED_LABEL}>
                          {cell?.phoneLabel ?? axisLabel(axis)}
                        </Box>
                      ) : null}
                      <Box sx={CELL_LINE}>
                        {verdict ? <VerdictArrow down={rowIndex > 0} /> : null}
                        {cell?.icon ? (
                          <LandingImage src={cell.icon} sx={CELL_ICON} />
                        ) : null}
                        {cell?.value}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
